import { faWarning } from '@fortawesome/pro-duotone-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import useHTMLBodyScrollbar from 'Core/Hooks/useHTMLBodyScrollbar'
import { FullScreenModal } from 'V2Components'
import { createRef, getResourceName, useAccessCartProvider } from 'features/resources'
import { usePrivateKubeNamespaces } from 'features/targets'
import { useObjectRef } from 'infra/redux'
import { Button, RadioInput, TargetIcon, Typography } from 'procyon-ui'
import React, { useRef, useState } from 'react'
import { cn } from 'Utils/Helpers'

const KubeClusterRoleSidebarPicker = ({ onCancel, kubeCluster: kube, onContinue }) => {
  const isSelectedAppRolesUpdated = useRef(false)

  const { getKubeNamespaceAppRolesMap } = usePrivateKubeNamespaces()
  // Array of selected Approle names from kubeCluster.Spec.IAMRoles.Map
  const [selectedRole, setSelectedRole] = useState('')
  const [k8CartIndex, setK8CartIndex] = useState(0)
  const { getObjectRef } = useObjectRef(['kubeClusters', 'kubeNamespaces'])
  const { getResourceFromCart, addItemsToCart, updateCartQueueItem, cartItems } =
    useAccessCartProvider()

  const k8Rrscs = cartItems.filter((e) => e?.Resource.RefKind === 'KubeNamespace' && e?.Principal)

  const getRsrc = () => {
    if (kube) return kube
    if (k8Rrscs.length >= 1) {
      const cartItem = k8Rrscs[k8CartIndex]
      return getObjectRef(cartItem.Resource)
    }
  }

  const rsrc = getRsrc()

  const resourceInQueue = getResourceFromCart(createRef(rsrc))

  const getKubeIAMRolesMap = () => {
    if (!rsrc) return []
    const isNameSpace = rsrc.ObjectMeta.Kind === 'KubeNamespace'
    const map = isNameSpace ? getKubeNamespaceAppRolesMap(rsrc) : kube.Spec.IAMRoles.Map
    return map
  }

  const kubeIAMRolesMap = getKubeIAMRolesMap()
  const iamRoleNames = Object.keys(getKubeIAMRolesMap()).filter(
    (name) => !(rsrc.ObjectMeta.Kind === 'KubeNamespace' && name === 'ProcyonKubectlClusterAdmin')
  )

  const handleAddToQueue = () => {
    // True, when the policy/req is for k8 rsrcs and not for Kubenamespace or Cluster itself
    let isK8RsrcsPolicy = true
    let roles = [
      {
        // GCP use Service account type
        RefKind: rsrc?.Spec.CloudType === 'AWS' ? 'AppRole' : 'ServiceAccount',
        RefID: kubeIAMRolesMap[selectedRole]
      }
    ]

    if (resourceInQueue) {
      updateCartQueueItem({
        resourceRef: createRef(rsrc),
        roles
      })
      if (k8CartIndex < k8Rrscs.length - 1) {
        setK8CartIndex((s) => s + 1)
        return
      }
    } else {
      isK8RsrcsPolicy = false
      addItemsToCart({
        resourceRef: createRef(rsrc),
        roles
      })
    }
    setSelectedRole('')
    onContinue?.(isK8RsrcsPolicy, { roles, k8Ref: createRef(rsrc) })
  }

  /**
   * Populate approle names `selectedAppRoles` if resource is in queue
   */
  if (rsrc && resourceInQueue && !isSelectedAppRolesUpdated.current) {
    // Get all approle ids which are in queue
    const approleRefIDs = resourceInQueue.Roles.ObjectRef?.map((e) => e.RefID) || []
    const appRoleNames = []
    approleRefIDs.forEach((id) => {
      // Get the approle name and id from the kubecluster
      for (const [name, approleID] of Object.entries(getKubeIAMRolesMap())) {
        // If the approle is selected in resource queue, push the approle name
        if (approleID === id) appRoleNames.push(name)
      }
    })
    // Update the approles with the ones in queue
    if (appRoleNames.length === 1) setSelectedRole(appRoleNames[0])
    isSelectedAppRolesUpdated.current = true
  }

  useHTMLBodyScrollbar(true)

  return (
    <FullScreenModal showModal={true}>
      <div className='flex justify-center items-center h-[100%] bg-[#2229455a]'>
        <div className='w-[626px] rounded-lg p-6 bg-white'>
          <div className=''>
            <div className='flex items-center gap-2 mb-1'>
              <TargetIcon type='KUBE_POD' />
              <Typography variant='h3'>
                Create policy for <strong>{getResourceName(rsrc)}</strong>
              </Typography>
            </div>
            <Typography className='text-[#667085]' variant='body-regular'>
              Select an AppRole to define the access permissions for this policy.
            </Typography>
            <div className='mt-6'>
              {!iamRoleNames.length && (
                <div className='p-4 rounded bg-red-500 flex gap-4 items-center'>
                  <FontAwesomeIcon color='white' icon={faWarning} />
                  <Typography className='!text-white' variant='body-regular'>
                    AppRoles for this cluster was not found. Please refresh and try.{' '}
                  </Typography>
                </div>
              )}
              {iamRoleNames.map((roleName) => {
                const isSelected = roleName === selectedRole
                return (
                  <div
                    key={roleName}
                    onClick={() => setSelectedRole(roleName)}
                    className={cn(
                      'flex items-center px-4 py-2 mb-3 border rounded-md cursor-pointer transition-all duration-200',
                      isSelected
                        ? 'border-[#3267D6] bg-[#F0F5FF]'
                        : 'border-[#EAECEF] hover:border-[#D0D5DD] bg-white'
                    )}
                  >
                    <RadioInput
                      id={roleName}
                      checked={isSelected}
                      name='role-selection'
                      onChange={() => setSelectedRole(roleName)}
                      value={roleName}
                      label={
                        <Typography
                          className={cn('transition-colors duration-200', {
                            '!text-[#3267D6] font-semibold': isSelected,
                            'text-[#344054]': !isSelected
                          })}
                          variant='body-regular'
                        >
                          {roleName}
                        </Typography>
                      }
                    />
                  </div>
                )
              })}

              <div className='flex justify-end gap-4 mt-10'>
                <Button onClick={onCancel} variant='gray'>
                  Cancel
                </Button>
                <Button onClick={handleAddToQueue} disabled={selectedRole == ''} variant='primary'>
                  {resourceInQueue ? 'Update AppRoles' : 'Add To Queue'}
                </Button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </FullScreenModal>
  )
}

export { KubeClusterRoleSidebarPicker }
